'use client';
import { useEffect, useState } from 'react';
import { getMyBookings, cancelBooking } from 'features/booking/bookingApi';
import { Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faClipboardList,
  faCalendarCheck,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import UpcomingShiftsCard from './components/UpcomingShiftsCard';
import BookingHistoryCard from './components/BookingHistoryCard';

interface Booking {
  booking_id: number;
  status: string;
  date: string;
  start_time: string;
  end_time: string;
  location: string;
  locum_role: string;
  pay_rate: number;
}

const LocumDashboardPage = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelingId, setCancelingId] = useState<number | null>(null);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const data = await getMyBookings();
      setBookings(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const handleCancel = async (bookingId: number) => {
    try {
      setCancelingId(bookingId);
      await cancelBooking(bookingId);

      // Update UI instantly
      setBookings(prev =>
        prev.map(b =>
          b.booking_id === bookingId ? { ...b, status: 'cancelled' } : b
        )
      );
    } catch (err: any) {
      alert(err.message);
    } finally {
      setCancelingId(null);
    }
  };

  // Calculate summary statistics
  const summary = bookings.reduce(
    (acc, booking) => {
      if (booking.status === 'confirmed') acc.confirmed += 1;
      if (booking.status === 'completed') acc.completed += 1;
      if (booking.status === 'cancelled') acc.cancelled += 1;
      return acc;
    },
    { confirmed: 0, completed: 0, cancelled: 0 }
  );

  return (
    <Container className="py-2 py-lg-4">
      {/* Header Section */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <p className="text-body-tertiary text-uppercase fs-10 fw-bold mb-1">
                Locum Workspace
              </p>
              <h2 className="text-body-emphasis mb-1">Dashboard</h2>
              <p className="mb-0 text-body-tertiary fs-9">
                Manage and track your upcoming shifts and booking history.
              </p>
            </div>
          </div>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" className="mb-0">
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Summary Stats */}
      {!loading && (
        <Row className="g-3 mb-4">
          {/* Confirmed */}
          <Col sm={6} lg={3}>
            <div className="p-3 rounded-3 bg-body border border-translucent h-100 transition-all hover-card">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-body-tertiary text-uppercase fw-bold">
                  Confirmed
                </small>
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="text-success"
                />
              </div>

              <h3 className="mb-0 fw-semibold text-success">
                {summary.confirmed}
              </h3>
            </div>
          </Col>

          {/* Completed */}
          <Col sm={6} lg={3}>
            <div className="p-3 rounded-3 bg-body border border-translucent h-100 transition-all hover-card">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-body-tertiary text-uppercase fw-bold">
                  Completed
                </small>
                <FontAwesomeIcon icon={faCalendarCheck} className="text-info" />
              </div>

              <h3 className="mb-0 fw-semibold text-info">
                {summary.completed}
              </h3>
            </div>
          </Col>

          {/* Cancelled */}
          <Col sm={6} lg={3}>
            <div className="p-3 rounded-3 bg-body border border-translucent h-100 transition-all hover-card">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-body-tertiary text-uppercase fw-bold">
                  Cancelled
                </small>
                <FontAwesomeIcon icon={faTimes} className="text-danger" />
              </div>

              <h3 className="mb-0 fw-semibold text-danger">
                {summary.cancelled}
              </h3>
            </div>
          </Col>

          {/* Total */}
          <Col sm={6} lg={3}>
            <div className="p-3 rounded-3 bg-body border border-translucent h-100 transition-all hover-card">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-body-tertiary text-uppercase fw-bold">
                  Total Bookings
                </small>
                <FontAwesomeIcon
                  icon={faClipboardList}
                  className="text-secondary"
                />
              </div>

              <h3 className="mb-0 fw-semibold text-body-emphasis">
                {bookings.length}
              </h3>
            </div>
          </Col>
        </Row>
      )}

      {/* Main Content Sections */}
      <Row className="g-4">
        <Col lg={6}>
          <UpcomingShiftsCard
            bookings={bookings}
            loading={loading}
            onCancel={handleCancel}
            cancelingId={cancelingId}
          />
        </Col>
        <Col lg={6}>
          <BookingHistoryCard bookings={bookings} loading={loading} />
        </Col>
      </Row>
    </Container>
  );
};

export default LocumDashboardPage;
